/**
 * Carousel / Instagram Posts API Service
 *
 * Talks to the shared carousel route (/api/v1/thumbnail/carousel/*). A single
 * Instagram post is just a carousel with slide_count === 1 — same endpoints.
 *
 * All calls use getApiBaseUrl() so they hit the same server that owns the
 * in-memory job store (desktop -> local Flask, web -> serverless API).
 */

import { getApiBaseUrl } from './urlResolver';
import { requestWrapped, getAuthHeadersFromStorage } from '../utils/httpUtils';
import type { ApiResponse } from '../types';

export interface CarouselSlide {
  index: number;
  url: string; // relative slide-serving path
  image_prompt: string;
  overlay_texts: string[];
}

export interface CarouselStatus {
  carousel_id: string;
  // Wire vocabulary is canonical (shared/protocols/job_store.py): terminal failure is 'failed'.
  status: 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  slide_count: number;
  caption: string;
  error: string | null;
  slides: CarouselSlide[];
  zip_url: string | null;
}

export interface CarouselGenerateParams {
  topic: string;
  slideCount: number;
  formatPreset: string;
  language?: string;
  provider?: string;
  model?: string;
  /** OpenRouter model for the caption/plan LLM (undefined = backend default). */
  llmModel?: string;
  brandKitId?: string;
}

export interface SlideCountSuggestion {
  slide_count: number;
  outline: string[];
}

/** Resolve a relative carousel URL (slide/zip) to an absolute one. */
export function resolveCarouselUrl(relativePath: string): string {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const normalized = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  return `${base}${normalized}`;
}

export const carouselAPI = {
  /** Start async carousel generation. Returns the carousel_id to poll. */
  async generate(
    params: CarouselGenerateParams
  ): Promise<ApiResponse<{ carousel_id: string; slide_count: number; format_preset: string }>> {
    const baseUrl = getApiBaseUrl().replace(/\/$/, '');
    return requestWrapped(
      `${baseUrl}/api/v1/thumbnail/carousel/generate`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          topic: params.topic,
          slide_count: params.slideCount,
          format_preset: params.formatPreset,
          language: params.language ?? 'en',
          provider: params.provider ?? null,
          model: params.model ?? null,
          llm_model: params.llmModel ?? null,
          brand_kit_id: params.brandKitId ?? null,
        }),
      },
      'Failed to start carousel'
    );
  },

  /** Poll generation status (progress + slides + caption when done). */
  async getStatus(carouselId: string): Promise<ApiResponse<CarouselStatus>> {
    const baseUrl = getApiBaseUrl().replace(/\/$/, '');
    return requestWrapped(
      `${baseUrl}/api/v1/thumbnail/carousel/${carouselId}/status`,
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get status'
    );
  },

  /** AI-suggest a slide count (1-10) + beat outline from the subject. */
  async suggestCount(topic: string, language = 'en'): Promise<ApiResponse<SlideCountSuggestion>> {
    const baseUrl = getApiBaseUrl().replace(/\/$/, '');
    return requestWrapped(
      `${baseUrl}/api/v1/thumbnail/carousel/suggest-count`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({ topic, language }),
      },
      'Failed to suggest count'
    );
  },
};
